import { Container, FederatedPointerEvent, Graphics, Text } from 'pixi.js';
import { FONT_UI, PALETTE } from './theme';

export interface ButtonConfig {
  text: string;
  width?: number;
  height?: number;
  backgroundColor?: number;
  hoverColor?: number;
  textColor?: number;
  fontSize?: number;
  borderColor?: number;
}

export class Button extends Container {
  private background: Graphics;
  private shadow: Graphics;
  private label: Text;
  private config: Required<ButtonConfig>;
  private onClickCallback: (() => void) | null = null;
  private isHovered: boolean = false;
  private isPressed: boolean = false;

  constructor(config: ButtonConfig) {
    super();

    this.config = {
      text: config.text,
      width: config.width || 200,
      height: config.height || 60,
      backgroundColor: config.backgroundColor ?? PALETTE.accent,
      hoverColor: config.hoverColor ?? PALETTE.accentHover,
      textColor: config.textColor ?? PALETTE.night,
      fontSize: config.fontSize || 24,
      borderColor: config.borderColor ?? 0xffffff,
    };

    this.shadow = new Graphics();
    this.addChild(this.shadow);

    this.background = new Graphics();
    this.addChild(this.background);

    this.label = new Text(this.config.text, {
      fontFamily: FONT_UI,
      fontSize: this.config.fontSize,
      fill: this.config.textColor,
      fontWeight: 'bold',
      letterSpacing: 1.5,
    });
    this.label.anchor.set(0.5);
    this.label.x = this.config.width / 2;
    this.label.y = this.config.height / 2 - 1;
    this.addChild(this.label);

    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerover', this.handlePointerOver);
    this.on('pointerout', this.handlePointerOut);
    this.on('pointerdown', this.handlePointerDown);
    this.on('pointerup', this.handlePointerUp);
    this.on('pointerupoutside', this.handlePointerUp);

    this.updateBackground(this.config.backgroundColor);
  }

  private updateBackground(color: number): void {
    const radius = this.config.height / 2;
    const outlineColor = this.isHovered ? this.config.borderColor : 0xffffff;
    const shadowOffset = this.isPressed ? 4 : 10;

    this.shadow.clear();
    this.shadow.beginFill(0x000000, 0.22);
    this.shadow.drawRoundedRect(0, shadowOffset, this.config.width, this.config.height, radius);
    this.shadow.endFill();

    this.background.clear();
    this.background.lineStyle(1.5, outlineColor, this.isHovered ? 0.65 : 0.28);
    this.background.beginFill(color);
    this.background.drawRoundedRect(0, 0, this.config.width, this.config.height, radius);
    this.background.endFill();

    this.background.lineStyle(1, 0xffffff, this.isHovered ? 0.3 : 0.15);
    this.background.moveTo(24, 13);
    this.background.lineTo(this.config.width - 24, 13);
  }

  private handlePointerOver = (_event: FederatedPointerEvent): void => {
    this.isHovered = true;
    this.updateBackground(this.config.hoverColor);
    this.scale.set(1.03);
  };

  private handlePointerOut = (_event: FederatedPointerEvent): void => {
    this.isHovered = false;
    this.isPressed = false;
    this.updateBackground(this.config.backgroundColor);
    this.scale.set(1.0);
  };

  private handlePointerDown = (_event: FederatedPointerEvent): void => {
    this.isPressed = true;
    this.updateBackground(this.config.hoverColor);
    this.scale.set(0.99);

    if (this.onClickCallback) {
      this.onClickCallback();
    }
  };

  private handlePointerUp = (_event: FederatedPointerEvent): void => {
    if (!this.isPressed) return;

    this.isPressed = false;
    this.updateBackground(this.isHovered ? this.config.hoverColor : this.config.backgroundColor);
    this.scale.set(this.isHovered ? 1.03 : 1.0);
  };

  onClick(callback: () => void): void {
    this.onClickCallback = callback;
  }

  setText(text: string): void {
    this.label.text = text;
  }

  enable(): void {
    this.eventMode = 'static';
    this.alpha = 1.0;
    this.cursor = 'pointer';
  }

  disable(): void {
    this.eventMode = 'none';
    this.alpha = 0.5;
    this.cursor = 'default';
  }

  setEnabled(enabled: boolean): void {
    if (enabled) {
      this.enable();
    } else {
      this.disable();
    }
  }
}
